/**
 * CrowdMeter — an analog gauge for how big the exit will be.
 *
 * Three zones, the owner's call: blue is cold, amber is the middle, red is a
 * big night. The needle is `crowd.level` (0–100) from the API, cut from the
 * same number as the demand band, so the needle and the words beside it can
 * never disagree. The needle is drawn in the text colour: the zones carry
 * the meaning, the needle only points.
 *
 * Colour is never the only signal (docs/11 §5): every size is paired with
 * the band in words — inside the component at 'lg' and 'md', beside it in
 * the row at 'sm'.
 */
import type { Opp } from './api.js';
import { demandLabel } from './format.js';

export type CrowdZone = 'cold' | 'warm' | 'hot';

export function crowdZone(level: number): CrowdZone {
  return level < 33 ? 'cold' : level < 66 ? 'warm' : 'hot';
}

const W = 200;
const CX = 100;
const CY = 100;
const R = 80;          // centre-line radius of the coloured band

/** Level 0 is due west, 100 due east, sweeping over the top. */
function at(level: number, r: number): [number, number] {
  const a = Math.PI * (1 - Math.max(0, Math.min(100, level)) / 100);
  return [CX + r * Math.cos(a), CY - r * Math.sin(a)];
}
function arc(from: number, to: number, r: number): string {
  const [x0, y0] = at(from, r);
  const [x1, y1] = at(to, r);
  return `M ${x0.toFixed(2)} ${y0.toFixed(2)} A ${r} ${r} 0 0 1 ${x1.toFixed(2)} ${y1.toFixed(2)}`;
}

const ZONES: { zone: CrowdZone; from: number; to: number }[] = [
  { zone: 'cold', from: 0, to: 32 },
  { zone: 'warm', from: 34, to: 65 },
  { zone: 'hot', from: 67, to: 100 },
];
const TICKS = [16.5, 33, 49.5, 66, 83];

export function CrowdDial({ level, size }: { level: number; size: 'lg' | 'md' | 'sm' }) {
  const zone = crowdZone(level);
  const band = size === 'sm' ? 22 : 18;
  const [nx, ny] = at(level, R - (size === 'sm' ? 2 : 6));
  // A needle with a tail: a short stub behind the hub reads as a real gauge.
  const [tx, ty] = at(level, -14);
  return (
    <svg className={`dial ${size}`} viewBox={`0 0 ${W} 116`} aria-hidden="true" focusable="false">
      {ZONES.map((z) => (
        <path key={z.zone} d={arc(z.from, z.to, R)} className={`zone ${z.zone}${z.zone === zone ? ' on' : ''}`}
          strokeWidth={band} fill="none" strokeLinecap="butt" />
      ))}
      {size !== 'sm' && TICKS.map((t) => {
        const [x0, y0] = at(t, R - band / 2 - 3);
        const [x1, y1] = at(t, R - band / 2 - (t % 33 === 0 ? 11 : 7));
        return <line key={t} x1={x0} y1={y0} x2={x1} y2={y1} className="tick" />;
      })}
      <line x1={tx} y1={ty} x2={nx} y2={ny} className="needle" strokeWidth={size === 'sm' ? 9 : 5} strokeLinecap="round" />
      <circle cx={CX} cy={CY} r={size === 'sm' ? 13 : 9} className="hub" />
      {size !== 'sm' && <circle cx={CX} cy={CY} r={3.5} className={`pip ${zone}`} />}
    </svg>
  );
}

/** How many people, in words a driver says: "about 19,200 people". */
export function headcount(n: number | null): string | null {
  if (n === null || n <= 0) return null;
  const rounded = n >= 1000 ? Math.round(n / 100) * 100 : Math.round(n / 10) * 10;
  return `about ${rounded.toLocaleString()} people`;
}

export function CrowdMeter({ opp, size }: { opp: Opp; size: 'lg' | 'md' }) {
  const zone = crowdZone(opp.crowd.level);
  const label = demandLabel(opp.demand_band);
  const people = headcount(opp.crowd.attendance_est);
  return (
    <div className={`meter ${size}`} role="img"
      aria-label={`Crowd meter: ${label}${people ? `, ${people}` : ''}`}>
      <div className="dial-wrap">
        <CrowdDial level={opp.crowd.level} size={size} />
        {size === 'lg' && (
          <div className="dial-ends" aria-hidden="true"><span>Quiet</span><span>Packed</span></div>
        )}
      </div>
      <div className="meter-text" aria-hidden="true">
        <span className={`meter-label ${zone}`}>{label}</span>
        {people && <span className="meter-sub num">{people}</span>}
      </div>
    </div>
  );
}
